import React, { useMemo, useState } from "react";
import SettlementVariantReturnsCard from "./SettlementVariantReturnsCard.js";
import "../../../css/Finance/SettlementProposedReturnsSection.css";
import useApi from "../../CustomHooks/useApi.js";
import Loading from "../../../Loading.js";
import SETTLEMENT_CALCULATION_MODES from "../../../Enums/SettlementCalculationModes";
import { useToast } from "../../../Contexts/ToastContext";

const SettlementProposedReturnsSection = ({
  tripGuid,
  financeAPI,
  draft,
  refreshKey = 0
}) => {
  const [data, setData] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const [hasCalculatedOnce, setHasCalculatedOnce] = useState(false);
  const [lastCalculatedAt, setLastCalculatedAt] = useState(null);
  const [lastCalculatedMode, setLastCalculatedMode] = useState(null);

  const [calculationMode, setCalculationMode] = useState(
    SETTLEMENT_CALCULATION_MODES.BY_VARIANTS
  );

  const { callApi } = useApi();
  const { showToast } = useToast();

  const getExcelErrorMessage = (error) => {
    const responseData = error?.response?.data;

    if (responseData instanceof ArrayBuffer) {
      try {
        const decoded = new TextDecoder("utf-8").decode(responseData);
        const parsed = JSON.parse(decoded);

        return (
          parsed?.message ||
          parsed?.Message ||
          parsed?.title ||
          "Nie udało się pobrać pliku Excel."
        );
      } catch {
        return "Nie udało się pobrać pliku Excel.";
      }
    }

    return (
      responseData?.message ||
      responseData?.Message ||
      responseData?.title ||
      responseData ||
      "Nie udało się pobrać pliku Excel."
    );
  };

  const calculateReturns = async () => {
    setIsCalculating(true);

    try {
      const result = await callApi(
        () =>
          financeAPI.getProposedSettlementReturns(tripGuid, draft?.createdByUserId, calculationMode),
        async (responseData) => {
          setData(responseData);
          setHasCalculatedOnce(true);
          setLastCalculatedAt(new Date());
          setLastCalculatedMode(calculationMode);
        }
      );

      return result;
    } finally {
      setIsCalculating(false);
    }
  };

  const safeFileName = (value) =>
  (value || "brak")
    .replace(/[\\/:*?"<>|]/g, "-")
    .replace(/\s+/g, "_")
    .trim();

  const handleExportToExcel = async () => {
    if (!lastCalculatedMode) return;

    try {
      setIsExportingExcel(true);

      const response = await financeAPI.settlementWorkbookPreview(
        tripGuid,
        draft?.createdByUserId,
        lastCalculatedMode
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const modeName =
  lastCalculatedMode === SETTLEMENT_CALCULATION_MODES.BY_VARIANTS
    ? "warianty"
    : "lacznie";

    const fileName = `${safeFileName(draft?.tripName)}-${modeName}-${safeFileName(
      draft?.createdByName
    )}.xlsx`;

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");

      a.href = url;
      a.download = fileName;

      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      showToast(getExcelErrorMessage(error), "error");
    } finally {
      setIsExportingExcel(false);
    }
  };

  const handleModeChange = (mode) => {
    setCalculationMode(mode);
    setData(null);
    setHasCalculatedOnce(false);
    setLastCalculatedAt(null);
    setLastCalculatedMode(null);
  };

  const lastCalculatedLabel = useMemo(() => {
    if (!lastCalculatedAt) return null;

    return lastCalculatedAt.toLocaleString("pl-PL", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }, [lastCalculatedAt]);

  

  return (
    <div className="settlement-proposed-returns-section finance-section-card">
      <div className="settlement-proposed-returns-header">
        <div>
          <h3>Proponowane zwroty</h3>
          <p className="settlement-proposed-returns-subtitle">
            Zwroty mogą być liczone osobno dla każdego wariantu albo globalnie
            dla całej wycieczki. Przedstawione wyliczenia mają charakter
            poglądowy i powinny stanowić jedynie pomoc przy rozliczeniu.
            Ostateczna decyzja odnośnie zwrotów należy do osoby koordynatorskiej.
            Pamiętajmy, że głównym celem jest bilansowanie rozliczenia do 0 zł.
          </p>
        </div>
      </div>

      <div className="settlement-calculation-mode-box">
        <div className="settlement-calculation-mode-left">
          <div className="settlement-calculation-mode-label">
            Tryb rozliczenia:
          </div>

          <div className="settlement-calculation-mode-toggle">
            <button
              type="button"
              className={
                calculationMode === SETTLEMENT_CALCULATION_MODES.BY_VARIANTS
                  ? "settlement-mode-button active"
                  : "settlement-mode-button"
              }
              onClick={() =>
                handleModeChange(SETTLEMENT_CALCULATION_MODES.BY_VARIANTS)
              }
              disabled={isCalculating || isExportingExcel}
            >
              Per wariant
            </button>

            <button
              type="button"
              className={
                calculationMode === SETTLEMENT_CALCULATION_MODES.GLOBAL
                  ? "settlement-mode-button active"
                  : "settlement-mode-button"
              }
              onClick={() =>
                handleModeChange(SETTLEMENT_CALCULATION_MODES.GLOBAL)
              }
              disabled={isCalculating || isExportingExcel}
            >
              Globalnie
            </button>
          </div>
        </div>

        <div className="settlement-proposed-returns-actions">
          <button
            type="button"
            className="settlement-recalculate-button"
            onClick={calculateReturns}
            disabled={isCalculating || isExportingExcel}
          >
            {isCalculating
              ? "Przeliczanie..."
              : hasCalculatedOnce
              ? "Przelicz ponownie"
              : "Przelicz"}
          </button>
        </div>
      </div>

        {hasCalculatedOnce && lastCalculatedLabel && !isCalculating && (
          <div className="settlement-last-calculated">
            <div>
              Ostatnie przeliczenie: <strong>{lastCalculatedLabel}</strong>
            </div>

            {lastCalculatedMode && (
              <button
                type="button"
                className="settlement-export-excel-button"
                onClick={handleExportToExcel}
                disabled={isCalculating || isExportingExcel}
              >
                {isExportingExcel ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin" />
                    <span>Pobieranie...</span>
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-file-excel" />
                    <span>
                      Eksportuj
                    </span>
                  </>
                )}
              </button>
            )}
          </div>
        )}

      {isCalculating ? (
        <Loading text="Przeliczanie rozliczenia..." />
      ) : data ? (
        <>
          {data.hasAnyNegativeVariantBalance && (
            <div className="settlement-error-box">{data.warningMessage}</div>
          )}

          <div className="settlement-variants-list">
            {data.variants?.length > 0 ? (
              data.variants.map((variant) => (
                <SettlementVariantReturnsCard
                  key={variant.variantGuid}
                  variant={variant}
                />
              ))
            ) : (
              <div className="settlement-empty-box">
                Brak danych do wyświetlenia.
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
};

export default SettlementProposedReturnsSection;